import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus, Save, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchSelect } from '@/components/ui/search-select';
import { RemoteSearchSelect } from '@/components/ui/remote-search-select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { EnCodeOption, MatiereNiveauGlobalView } from '../api/migration-v1-v2';

const NONE_VALUE = '__none__';

interface MatiereRow {
  /** Identité stable de la ligne (survit au renommage). */
  id: string;
  label: string;
}

interface NiveauCol {
  label: string;
  mef: string | null;
}

/** Cellule = intersection (matière, niveau). Clé locale par id de ligne + index niveau. */
const cellId = (matiereId: string, niveauIdx: number) => `${matiereId}::${niveauIdx}`;

/** Clé d'appariement par libellés (matière, niveau) — indépendante de la
 *  normalisation de clé backend (qui diffère selon l'environnement iconv). */
const labelPair = (matiere: string, niveau: string) => `${matiere}::${niveau}`;

/** Formate une option BCN pour l'affichage (badge langue vivante inclus). */
function enOptionLabel(o: EnCodeOption): string {
  if (o.isLangueVivante) {
    const rang = o.rang ? `LV${o.rang}` : 'LV';
    const type = (o.typeLangueRequis ?? [])
      .map((t) => (t === 'LVE' ? 'étrangère' : t === 'LVR' ? 'régionale' : t))
      .join('/');
    return `${o.label} · ${rang}${type ? ` ${type}` : ''} (${o.code})`;
  }
  return `${o.label} (${o.code})`;
}

/** Options du select recherchable d'une cellule : « aucun code » + options du
 *  MEF (matières + langues concrètes LV1/LV2/LV3), texte de recherche enrichi
 *  (langue, rang). Réinjecte le code courant s'il n'est plus proposé. */
function buildCellOptions(
  options: EnCodeOption[],
  currentCode: string | null,
  currentLabel: string | null
): { value: string; label: string; searchText?: string }[] {
  const opts: { value: string; label: string; searchText?: string }[] = [
    { value: NONE_VALUE, label: 'Aucun code' },
    ...options.map((o) => ({
      value: o.code,
      label: enOptionLabel(o),
      searchText: [o.label, o.code, o.langueLibelle ?? '', o.isLangueVivante ? `LV${o.rang ?? ''}` : '']
        .filter(Boolean)
        .join(' '),
    })),
  ];
  if (currentCode && !options.some((o) => o.code === currentCode)) {
    opts.push({ value: currentCode, label: `${currentLabel ?? currentCode} (${currentCode})`, searchText: currentCode });
  }
  return opts;
}

/**
 * Éditeur du **référentiel global matière × niveau** (défauts appliqués à
 * toutes les écoles). La nomenclature BCN étant définie par MEF, une même
 * discipline porte un code différent selon le niveau : le référentiel associe
 * donc chaque (matière, niveau) à UN code BCN (la « matière V2 » n'est plus
 * mappée — le nom V2 se déduit de la nomenclature BCN).
 *
 * Les niveaux (et leur MEF) proviennent du catalogue global (onglet Niveaux) ;
 * le choix BCN de chaque cellule est contraint aux matières associables au MEF
 * du niveau (data API `mef_matieres`), avec repli sur la recherche libre quand
 * le niveau ne porte pas de MEF. Un pré-mapping IA aide à remplir la grille.
 */
export function MatiereConfigTab() {
  const [niveaux, setNiveaux] = useState<NiveauCol[]>([]);
  const [mefOptions, setMefOptions] = useState<Record<string, EnCodeOption[]>>({});
  const [matieres, setMatieres] = useState<MatiereRow[]>([]);
  const [codes, setCodes] = useState<Record<string, string | null>>({});
  // Libellé de la matière BCN par cellule (affichage « nom (code) » hors MEF).
  const [codeLabels, setCodeLabels] = useState<Record<string, string | null>>({});
  const [aiCells, setAiCells] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiCount, setAiCount] = useState(0);
  const counter = useRef(0);

  const newId = () => `m-${counter.current++}`;

  const seedFromView = useCallback((view: MatiereNiveauGlobalView) => {
    setNiveaux(view.niveaux);
    setMefOptions(view.mefOptions);
    const rows = view.matieres.map((label) => ({ id: newId(), label }));
    setMatieres(rows);

    const codeByPair = new Map<string, string>();
    const labelByPair = new Map<string, string>();
    for (const a of view.associations) {
      if (a.enCode) {
        codeByPair.set(`${a.matiere}::${a.niveau}`, a.enCode);
        if (a.enLabel) labelByPair.set(`${a.matiere}::${a.niveau}`, a.enLabel);
      }
    }
    const seed: Record<string, string | null> = {};
    const seedLabels: Record<string, string | null> = {};
    for (const m of rows) {
      view.niveaux.forEach((n, i) => {
        const pair = `${m.label}::${n.label}`;
        const c = codeByPair.get(pair);
        if (c) {
          seed[cellId(m.id, i)] = c;
          seedLabels[cellId(m.id, i)] = labelByPair.get(pair) ?? null;
        }
      });
    }
    setCodes(seed);
    setCodeLabels(seedLabels);
    setAiCells(new Set());
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      seedFromView(await migrationV1V2Api.getMatiereNiveauGlobalView());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [seedFromView]);

  useEffect(() => {
    void load();
  }, [load]);

  const setCellCode = (cid: string, code: string | null, label?: string | null) => {
    setCodes((prev) => ({ ...prev, [cid]: code }));
    setCodeLabels((prev) => ({ ...prev, [cid]: code ? label ?? null : null }));
    setAiCells((prev) => {
      if (!prev.has(cid)) return prev;
      const next = new Set(prev);
      next.delete(cid);
      return next;
    });
    setDirty(true);
    setSaved(false);
  };

  const addMatiere = () => {
    setMatieres((prev) => [...prev, { id: newId(), label: '' }]);
    setDirty(true);
    setSaved(false);
  };

  const renameMatiere = (id: string, label: string) => {
    setMatieres((prev) => prev.map((m) => (m.id === id ? { ...m, label } : m)));
    setDirty(true);
    setSaved(false);
  };

  const removeMatiere = (id: string) => {
    setMatieres((prev) => prev.filter((m) => m.id !== id));
    const prune = (prev: Record<string, string | null>) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (k.startsWith(`${id}::`)) delete next[k];
      }
      return next;
    };
    setCodes(prune);
    setCodeLabels(prune);
    setDirty(true);
    setSaved(false);
  };

  const buildPayload = () => {
    const cleanMatieres = matieres.map((m) => m.label.trim()).filter((l) => l !== '');
    const associations: { matiere: string; niveau: string; enCode: string | null }[] = [];
    for (const m of matieres) {
      const label = m.label.trim();
      if (label === '') continue;
      niveaux.forEach((n, i) => {
        associations.push({ matiere: label, niveau: n.label, enCode: codes[cellId(m.id, i)] ?? null });
      });
    }
    return { matieres: cleanMatieres, associations };
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await migrationV1V2Api.saveMatiereNiveauGlobal(buildPayload());
      await load();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const runAi = async () => {
    const labels = matieres.map((m) => m.label.trim()).filter((l) => l !== '');
    if (labels.length === 0 || niveaux.length === 0) return;
    setAiRunning(true);
    setAiCount(0);
    setError('');
    try {
      const resp = await migrationV1V2Api.aiSuggestMatiereNiveauGlobal(labels);
      if (!resp.enabled || resp.suggestions.length === 0) return;
      // Appariement par libellés (matière, niveau) renvoyés par le backend —
      // pas par la clé normalisée (l'iconv prod normalise différemment le JS).
      const byPair = new Map(
        resp.suggestions
          .filter((s) => s.matiere != null && s.niveau != null)
          .map((s) => [labelPair(s.matiere as string, s.niveau as string), s])
      );
      let count = 0;
      const filled = new Set<string>();
      const filledLabels: Record<string, string | null> = {};
      setCodes((prev) => {
        const next = { ...prev };
        for (const m of matieres) {
          const label = m.label.trim();
          if (label === '') continue;
          niveaux.forEach((n, i) => {
            const cid = cellId(m.id, i);
            // On ne pré-remplit que les cellules encore vides.
            if (next[cid]) return;
            const s = byPair.get(labelPair(label, n.label));
            if (s?.enCode) {
              next[cid] = s.enCode;
              filledLabels[cid] = s.enLabel ?? null;
              filled.add(cid);
              count++;
            }
          });
        }
        return next;
      });
      if (count > 0) {
        setCodeLabels((prev) => ({ ...prev, ...filledLabels }));
        setAiCells((prev) => new Set([...prev, ...filled]));
        setDirty(true);
        setSaved(false);
      }
      setAiCount(count);
    } catch {
      /* best-effort */
    } finally {
      setAiRunning(false);
    }
  };

  const labelForCell = (mef: string | null, cid: string, code: string | null): string | null => {
    if (!code) return null;
    if (mef && mefOptions[mef]) {
      const opt = mefOptions[mef].find((o) => o.code === code);
      if (opt) return opt.label;
    }
    return codeLabels[cid] ?? null;
  };

  const mappedCount = useMemo(
    () => Object.values(codes).filter((c) => c).length,
    [codes]
  );
  const totalCells = matieres.filter((m) => m.label.trim()).length * niveaux.length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4" style={{ textTransform: 'none' }}>
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
          Référentiel global des matières par <strong>association matière × niveau</strong>.
          La nomenclature BCN étant définie par MEF, chaque (matière, niveau) reçoit son
          propre code BCN — les choix sont limités aux matières associables au MEF du niveau.
          Les niveaux et leur MEF proviennent de l'onglet <strong>Niveaux</strong>. À défaut
          de correspondance, le code de repli <strong>009900</strong> (« activités non
          spécialisées / divers ») est appliqué aux niveaux portant un MEF.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {totalCells > 0 && (
            <Badge variant={mappedCount === totalCells ? 'success' : 'warning'}>
              {mappedCount}/{totalCells} codés
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={runAi}
            disabled={aiRunning || matieres.every((m) => m.label.trim() === '') || niveaux.length === 0}
          >
            {aiRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Pré-mapper (IA)
          </Button>
          <Button onClick={handleSave} disabled={saving || !dirty}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <Check className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saved ? 'Sauvegardé' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      {aiCount > 0 && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm" style={{ color: '#5b21b6' }}>
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-violet-500" />
          {aiCount} code{aiCount > 1 ? 's' : ''} BCN suggéré{aiCount > 1 ? 's' : ''} par l'IA (✨) — vérifiez puis Enregistrer.
        </div>
      )}

      {niveaux.length === 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          Aucun niveau configuré. Renseignez d'abord les niveaux (et leur MEF) dans l'onglet
          <strong> Niveaux</strong> — ils pilotent les colonnes et le filtrage des codes BCN.
        </div>
      )}

      {niveaux.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '42%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                {['Matière V1', 'Niveau', 'MEF', 'Code BCN de l\'association', ''].map((h, i) => (
                  <th
                    key={i}
                    className={`px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 ${i === 4 ? 'text-right' : 'text-left'}`}
                  >
                    {i === 3 ? (
                      <>
                        {h} <span className="text-gray-400 font-normal">(Éd. Nationale)</span>
                      </>
                    ) : (
                      h
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matieres.map((m) => (
                <Fragment key={m.id}>
                  {niveaux.map((n, i) => {
                    const cid = cellId(m.id, i);
                    const code = codes[cid] ?? null;
                    const options = n.mef ? mefOptions[n.mef] ?? [] : [];
                    const currentLabel = labelForCell(n.mef, cid, code);
                    const isAi = aiCells.has(cid);
                    return (
                      <tr key={cid} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-3 py-1.5 align-middle">
                          {i === 0 ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                value={m.label}
                                placeholder="Libellé matière V1 (ex. Français)"
                                onChange={(ev) => renameMatiere(m.id, ev.target.value)}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Retirer la matière"
                                onClick={() => removeMatiere(m.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-gray-300 pl-2">〃</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 align-middle">{n.label}</td>
                        <td className="px-3 py-1.5 align-middle">
                          {n.mef ? (
                            <code className="text-xs text-gray-600 dark:text-gray-400">{n.mef}</code>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 align-middle">
                          <div className="flex items-center gap-1.5">
                            {isAi && <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-500" />}
                            {n.mef && options.length > 0 ? (
                              <SearchSelect
                                value={code ?? NONE_VALUE}
                                onValueChange={(v) =>
                                  setCellCode(
                                    cid,
                                    v === NONE_VALUE ? null : v,
                                    options.find((o) => o.code === v)?.label ?? null
                                  )
                                }
                                options={buildCellOptions(options, code, currentLabel)}
                                placeholder="Choisir un code…"
                                searchPlaceholder="Rechercher (langue, matière, code)…"
                                className={`h-8 text-xs ${isAi ? 'border-violet-400' : ''}`}
                                contentClassName="min-w-[24rem]"
                              />
                            ) : (
                              <RemoteSearchSelect
                                value={code ?? ''}
                                selectedLabel={currentLabel}
                                onSelect={(c, l) => setCellCode(cid, c || null, l)}
                                search={async (q) =>
                                  (await migrationV1V2Api.searchEnMatieres(q)).map((o) => ({
                                    code: o.code,
                                    label: enOptionLabel(o),
                                  }))
                                }
                                placeholder="Rechercher un code BCN…"
                                className={isAi ? 'border-violet-400' : ''}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right align-middle" />
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              {matieres.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                    Aucune matière. Ajoutez une matière pour commencer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Button variant="outline" onClick={addMatiere} disabled={niveaux.length === 0}>
        <Plus className="h-4 w-4" />
        Ajouter une matière
      </Button>
    </div>
  );
}
